import { useState, useEffect } from 'react';
import { backdropClose } from '../utils/backdropClose';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';
import { Info, AlertCircle } from 'lucide-react';

interface PauseModalProps {
  isOpen: boolean;
  onClose: () => void;
  membershipId: string;
  onSuccess: () => void;
}

interface PauseStats {
  pauseEnabled: boolean;
  pauseRules: {
    maxTotalDays: number | null;
    requireReason: boolean;
  };
  stats: {
    pauseCount: number;
    totalPausedDays: number;
    availableDays: number | null;
    hasActivePause: boolean;
  };
  membership: {
    id: string;
    status: string;
    planId: string;
    planName: string;
    startDate: string;
    endDate: string | null;
  };
}

export default function PauseModal({ isOpen, onClose, membershipId, onSuccess }: PauseModalProps) {
  const [loading, setLoading] = useState(false);
  const [loadingStats, setLoadingStats] = useState(true);
  const [stats, setStats] = useState<PauseStats | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen && membershipId) {
      fetchPauseStats();
      // Set default start date to today or membership start if future
      const today = new Date().toISOString().split('T')[0];
      setStartDate(today);
      setEndDate('');
      setReason('');
      setValidationErrors([]);
      setWarnings([]);
    }
  }, [isOpen, membershipId]);

  const fetchPauseStats = async () => {
    try {
      setLoadingStats(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const response = await fetch(`${API_BASE_URL}/api/membership-management/pause-stats/${membershipId}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.data);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to fetch pause stats');
      }
    } catch (error) {
      console.error('Error fetching pause stats:', error);
      toast.error('Failed to fetch pause stats');
    } finally {
      setLoadingStats(false);
    }
  };

  const validateDates = () => {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!startDate) {
      errors.push('Start date is required');
      return { errors, warnings };
    }

    if (!endDate) {
      errors.push('End date is required');
      return { errors, warnings };
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (start > end) {
      errors.push('Start date must be before end date');
    }

    if (start < today) {
      warnings.push('Start date is in the past');
    }

    const pauseDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;



    if (stats && stats.stats.availableDays !== null && pauseDays > stats.stats.availableDays) {
      errors.push(`Pause duration exceeds available pause days (${stats.stats.availableDays} days remaining)`);
    }

    if (stats && stats.pauseRules.requireReason && !reason.trim()) {
      errors.push('Reason is required for pause requests');
    }

    return { errors, warnings };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = validateDates();
    setValidationErrors(validation.errors);
    setWarnings(validation.warnings);

    if (validation.errors.length > 0) {
      return;
    }

    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Not authenticated');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/membership-management/pause`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          membershipId,
          startDate: new Date(startDate).toISOString(),
          endDate: new Date(endDate).toISOString(),
          reason: reason.trim() || undefined,
          autoApprove: true // Auto-approve for admin
        })
      });

      if (response.ok) {
        toast.success('Membership paused successfully');
        onSuccess();
        onClose();
      } else {
        const error = await response.json();
        if (error.errors && Array.isArray(error.errors)) {
          setValidationErrors(error.errors);
        } else {
          toast.error(error.error || 'Failed to pause membership');
        }
      }
    } catch (error) {
      console.error('Error pausing membership:', error);
      toast.error('Failed to pause membership');
    } finally {
      setLoading(false);
    }
  };

  const calculateDays = () => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  };

  if (!isOpen) return null;

  if (loadingStats) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
        <div className="bg-white rounded-lg p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading pause information...</p>
        </div>
      </div>
    );
  }

  if (!stats || !stats.pauseEnabled) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]" {...backdropClose(onClose)}>
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
          <h2 className="text-xl font-bold mb-4">Pause Not Available</h2>
          <p className="text-gray-600 mb-4">
            Pause feature is not enabled for this membership plan.
          </p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const pauseDays = calculateDays();

  if (!stats) {
    return null;
  }

  const availableDays = stats.stats.availableDays;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-2 sm:p-4" {...backdropClose(onClose)}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-4">Pause Membership</h2>
          <p className="text-gray-600 mb-6">Plan: {stats.membership.planName}</p>

          {/* Pause Stats */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-900 mb-3">Pause Allowance</h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-blue-600 font-medium">Allowed</div>
                <div className="text-gray-700">
                  {stats.pauseRules.maxTotalDays !== null
                    ? `${stats.pauseRules.maxTotalDays} days`
                    : 'Unlimited'}
                </div>
              </div>
              <div>
                <div className="text-blue-600 font-medium">Used</div>
                <div className="text-gray-700">{stats.stats.totalPausedDays} days</div>
              </div>
              <div>
                <div className="text-blue-600 font-medium">Available</div>
                <div className="text-gray-700 font-semibold">
                  {availableDays !== null
                    ? `${availableDays} days`
                    : 'Unlimited'}
                </div>
              </div>
            </div>

          </div>

          {/* Pause Rules Info */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-2">
              <Info className="h-5 w-5 text-gray-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-gray-700">
                <div className="font-semibold mb-2">Pause Rules:</div>
                <ul className="list-disc list-inside space-y-1">
                  {stats.pauseRules.maxTotalDays && (
                    <li>Maximum {stats.pauseRules.maxTotalDays} total pause days</li>
                  )}
                  {stats.pauseRules.requireReason && (
                    <li>Reason is required</li>
                  )}
                  <li>Membership expiry will be extended by pause duration</li>
                  {stats.membership.endDate && (
                    <li>
                      Valid pause range: {(() => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const memStart = new Date(stats.membership.startDate);
                        const start = today > memStart ? today : memStart;
                        return `${start.toLocaleDateString()} - ${new Date(stats.membership.endDate).toLocaleDateString()}`;
                      })()}
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-red-700">
                  <div className="font-semibold mb-1">Please fix the following errors:</div>
                  <ul className="list-disc list-inside space-y-1">
                    {validationErrors.map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Warnings */}
          {warnings.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-yellow-700">
                  <div className="font-semibold mb-1">Warnings:</div>
                  <ul className="list-disc list-inside space-y-1">
                    {warnings.map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date *
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                min={(() => {
                  const today = new Date().toISOString().split('T')[0];
                  const memStart = stats?.membership.startDate ? new Date(stats.membership.startDate).toISOString().split('T')[0] : today;
                  return today > memStart ? today : memStart;
                })()}
                max={stats?.membership.endDate ? new Date(stats.membership.endDate).toISOString().split('T')[0] : undefined}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date *
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setValidationErrors([]);
                  setWarnings([]);
                }}
                min={startDate || new Date().toISOString().split('T')[0]}
                max={availableDays !== null && startDate
                  ? new Date(new Date(startDate).getTime() + (availableDays - 1) * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                  : undefined}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              {pauseDays > 0 && (
                <p className="mt-1 text-sm text-gray-500">
                  Duration: {pauseDays} day{pauseDays !== 1 ? 's' : ''}
                  {availableDays !== null && pauseDays > availableDays && (
                    <span className="text-red-600 ml-2">(Exceeds available {availableDays} days)</span>
                  )}
                </p>
              )}
            </div>

            {stats.pauseRules.requireReason && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason *
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter reason for pause..."
                  required
                />
              </div>
            )}

            {!stats.pauseRules.requireReason && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason (Optional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter reason for pause (optional)..."
                />
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading || validationErrors.length > 0}
              >
                {loading ? 'Pausing...' : 'Pause Membership'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

